import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  CreditCard, Package, Bot, MapPin, Calendar, ChevronRight,
  Loader2, ShieldCheck, AlertTriangle, ArrowUpRight, ExternalLink, Sparkles
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const brandColors = {
  visa: "from-blue-500 to-blue-700",
  mastercard: "from-orange-500 to-red-600",
  amex: "from-emerald-500 to-teal-700",
  discover: "from-amber-500 to-orange-600",
  default: "from-slate-600 to-slate-800",
};
const brandLabel = {
  visa: "VISA", mastercard: "Mastercard", amex: "AmEx", discover: "Discover"
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      day: "2-digit", month: "long", year: "numeric"
    });
  } catch { return "—"; }
};

const statusBadge = (status, trial) => {
  if (trial) return { label: "In prova gratuita", color: "bg-amber-100 text-amber-700 border-amber-200" };
  if (status === "active") return { label: "Attivo", color: "bg-emerald-100 text-emerald-700 border-emerald-200" };
  if (status === "trialing") return { label: "Trial", color: "bg-amber-100 text-amber-700 border-amber-200" };
  if (status === "past_due") return { label: "Pagamento in ritardo", color: "bg-red-100 text-red-700 border-red-200" };
  if (status === "canceled") return { label: "Cancellato", color: "bg-gray-100 text-gray-700 border-gray-200" };
  return { label: "—", color: "bg-gray-100 text-gray-600 border-gray-200" };
};

const BillingOverview = () => {
  const navigate = useNavigate();
  const { token } = useAuth();

  const [data, setData] = useState(null);
  const [pm, setPm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [changingCard, setChangingCard] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      fetch(`${API}/billing/subscription`, { headers }).then(r => r.ok ? r.json() : null),
      fetch(`${API}/billing/payment-method`, { headers }).then(r => r.ok ? r.json() : null),
    ]).then(([sub, pmRes]) => {
      setData(sub);
      setPm(pmRes);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [token]);

  const handleSyncPaymentData = async () => {
    setSyncing(true);
    try {
      const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
      const res = await fetch(`${API}/billing/sync-payment-data`, { method: "POST", headers });
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.ok) {
        const pmRes = await fetch(`${API}/billing/payment-method`, { headers }).then(r => r.ok ? r.json() : null);
        setPm(pmRes);
        if (d.has_payment_method) {
          toast.success("Dati di pagamento recuperati e sincronizzati!");
        } else {
          toast.success("Account Stripe collegato. Nessuna carta trovata: aggiungine una qui sotto.");
        }
      } else {
        toast.error("Nessun dato di pagamento trovato su Stripe per questo account.");
      }
    } catch (e) {
      toast.error("Errore di rete durante la sincronizzazione");
    } finally {
      setSyncing(false);
    }
  };

  const handleUpdateCard = async () => {
    setChangingCard(true);
    try {
      const res = await fetch(`${API}/billing/setup-payment-method`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          success_url: window.location.href,
          cancel_url: window.location.href,
        }),
      });
      if (res.ok) {
        const d = await res.json();
        if (d.setup_url) { window.location.href = d.setup_url; return; }
      }
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || "Impossibile aprire la pagina di aggiornamento carta");
    } catch (e) {
      toast.error("Errore di rete");
    } finally {
      setChangingCard(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="billing-overview-loading">
        <Loader2 className="h-8 w-8 text-indigo-500 animate-spin" />
      </div>
    );
  }

  const sub = data?.subscription || {};
  const societa = data?.societa || {};
  const packages = sub.packages || [];
  const isTrial = sub.stripe_status === "trialing" || (sub.trial_end && new Date(sub.trial_end) > new Date());
  const isGratuito = sub.gratuito === true;
  const badge = statusBadge(sub.stripe_status, isTrial);

  const nextBillingDate = sub.trial_end || sub.current_period_end || sub.data_scadenza || "";
  const nextBillingLabel = isTrial && sub.trial_end
    ? `Primo addebito il ${formatDate(sub.trial_end)}`
    : sub.current_period_end
    ? `Prossimo rinnovo il ${formatDate(sub.current_period_end)}`
    : "—";

  return (
    <div className="min-h-screen p-4 md:p-8" style={{ background: "linear-gradient(145deg,#F8F8FC,#FFFFFF)" }} data-testid="billing-overview-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
            <Sparkles className="h-3.5 w-3.5" /> Riepilogo abbonamento
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-gray-900">
            Il tuo piano <span style={{ color: "#6C63FF" }}>Yollo</span>
          </h1>
          <p className="text-sm text-gray-500 mt-2">
            {societa.denominazione} — gestione self-service di piano, add-on e pagamenti
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">

          {/* CARD 1 — Piano attuale */}
          <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm" data-testid="billing-piano-card">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-indigo-100 flex items-center justify-center">
                  <Package className="h-5 w-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase">Piano</p>
                  <h2 className="text-base font-black text-gray-900">Pacchetti attivi</h2>
                </div>
              </div>
              <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${badge.color}`} data-testid="billing-status-badge">
                {badge.label}
              </span>
            </div>

            {packages.length === 0 ? (
              <p className="text-sm text-gray-400 italic">Nessun pacchetto attivo</p>
            ) : (
              <div className="space-y-2">
                {packages.map(p => (
                  <div key={p.id} className="flex items-center justify-between p-3 rounded-xl bg-gray-50">
                    <span className="text-sm font-semibold text-gray-800">{p.nome}</span>
                    <ShieldCheck className="h-4 w-4 text-emerald-500" />
                  </div>
                ))}
              </div>
            )}

            {isGratuito && (
              <div className="mt-4 p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-2">
                <Sparkles className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700">
                  Account demo gratuito — non vengono effettuati addebiti.
                </p>
              </div>
            )}
          </div>

          {/* CARD 2 — Add-on attivi */}
          <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm" data-testid="billing-addon-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-2xl bg-purple-100 flex items-center justify-center">
                <Bot className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase">Add-on</p>
                <h2 className="text-base font-black text-gray-900">Servizi extra</h2>
              </div>
            </div>

            <div className="space-y-2">
              {/* Bot Telegram */}
              <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50">
                <div className="flex items-center gap-2">
                  <Bot className="h-4 w-4 text-purple-500" />
                  <span className="text-sm font-semibold text-gray-800">Bot Telegram</span>
                </div>
                {sub.addon_telegram_qty > 0 || sub.telegram_attivo ? (
                  <span className="text-xs font-bold px-2 py-1 rounded-full bg-emerald-100 text-emerald-700" data-testid="addon-telegram-active">
                    {sub.addon_telegram_qty > 0 ? `x${sub.addon_telegram_qty}` : "Attivo"}
                  </span>
                ) : (
                  <span className="text-xs font-semibold text-gray-400">Non attivo</span>
                )}
              </div>

              {/* Locale extra */}
              <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-pink-500" />
                  <span className="text-sm font-semibold text-gray-800">Locali aggiuntivi</span>
                </div>
                {sub.addon_locale_extra > 0 ? (
                  <span className="text-xs font-bold px-2 py-1 rounded-full bg-emerald-100 text-emerald-700" data-testid="addon-locale-active">
                    x{sub.addon_locale_extra}
                  </span>
                ) : (
                  <span className="text-xs font-semibold text-gray-400">Solo locale principale</span>
                )}
              </div>
            </div>

            <button
              onClick={() => navigate("/abbonamento")}
              className="w-full mt-4 h-10 rounded-xl text-xs font-bold text-indigo-600 hover:bg-indigo-50 transition-colors border border-indigo-200 flex items-center justify-center gap-1.5"
              data-testid="billing-add-addon-btn"
            >
              <ArrowUpRight className="h-4 w-4" /> Aggiungi o modifica add-on
            </button>
          </div>

          {/* CARD 3 — Carta salvata */}
          <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm" data-testid="billing-card-section">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-2xl bg-emerald-100 flex items-center justify-center">
                <CreditCard className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase">Pagamento</p>
                <h2 className="text-base font-black text-gray-900">Metodo di pagamento</h2>
              </div>
            </div>

            {pm && pm.has_payment_method ? (
              <>
                {/* Carta visiva */}
                <div
                  className={`relative overflow-hidden rounded-2xl p-5 text-white bg-gradient-to-br ${brandColors[pm.brand?.toLowerCase()] || brandColors.default}`}
                  data-testid="billing-saved-card"
                >
                  <div className="flex justify-between items-start mb-8">
                    <CreditCard className="h-7 w-7 opacity-60" />
                    <span className="text-xs font-bold tracking-wider opacity-90">
                      {brandLabel[pm.brand?.toLowerCase()] || (pm.brand || "").toUpperCase()}
                    </span>
                  </div>
                  <p className="text-lg font-mono tracking-[0.2em] mb-4">
                    {pm.last4 ? `•••• •••• •••• ${pm.last4}` : (pm.email || pm.brand || "Metodo salvato")}
                  </p>
                  <div className="flex justify-between items-end text-xs opacity-85">
                    {pm.exp_month ? (
                      <div>
                        <p className="text-[10px] uppercase opacity-70">Scadenza</p>
                        <p className="font-semibold">{String(pm.exp_month).padStart(2, "0")}/{String(pm.exp_year).slice(-2)}</p>
                      </div>
                    ) : <div />}
                    <div className="text-right">
                      <p className="text-[10px] uppercase opacity-70">Intestatario</p>
                      <p className="font-semibold truncate max-w-[140px]">{societa.denominazione || "—"}</p>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleUpdateCard}
                  disabled={changingCard}
                  className="w-full mt-4 h-11 rounded-xl text-sm font-bold text-white transition-all disabled:opacity-60 flex items-center justify-center gap-1.5"
                  style={{ background: "linear-gradient(135deg,#6C63FF,#8B85FF)" }}
                  data-testid="billing-update-card-btn"
                >
                  {changingCard ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Apertura...</>
                  ) : (
                    <><CreditCard className="h-4 w-4" /> Aggiorna carta</>
                  )}
                </button>
              </>
            ) : (
              <div className="text-center py-6">
                <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-amber-100 flex items-center justify-center">
                  <AlertTriangle className="h-6 w-6 text-amber-600" />
                </div>
                <p className="text-sm font-bold text-gray-800 mb-1">Nessuna carta salvata</p>
                <p className="text-xs text-gray-500 mb-4">
                  Aggiungi un metodo di pagamento per continuare a usare Yollo dopo il trial.
                </p>
                <button
                  onClick={handleUpdateCard}
                  disabled={changingCard}
                  className="w-full h-11 rounded-xl text-sm font-bold text-white transition-all disabled:opacity-60 flex items-center justify-center gap-1.5"
                  style={{ background: "linear-gradient(135deg,#6C63FF,#8B85FF)" }}
                  data-testid="billing-add-card-btn"
                >
                  {changingCard ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Apertura...</>
                  ) : (
                    <><CreditCard className="h-4 w-4" /> Aggiungi carta</>
                  )}
                </button>
                <button
                  onClick={handleSyncPaymentData}
                  disabled={syncing}
                  className="w-full mt-2 h-10 rounded-xl text-xs font-bold text-indigo-600 hover:bg-indigo-50 transition-colors border border-indigo-200 flex items-center justify-center gap-1.5 disabled:opacity-60"
                  data-testid="billing-sync-payment-btn"
                >
                  {syncing ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Sincronizzazione...</>
                  ) : (
                    <>Ho già pagato — Sincronizza dati pagamento</>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* CARD 4 — Prossimo rinnovo */}
          <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-sm" data-testid="billing-renewal-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-2xl bg-amber-100 flex items-center justify-center">
                <Calendar className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase">Fatturazione</p>
                <h2 className="text-base font-black text-gray-900">Prossimo addebito</h2>
              </div>
            </div>

            <div className="text-center py-4">
              <p className="text-3xl font-black text-gray-900 mb-1" data-testid="billing-next-date">
                {formatDate(nextBillingDate) !== "—" ? formatDate(nextBillingDate) : "Non disponibile"}
              </p>
              <p className="text-xs text-gray-500">{nextBillingLabel}</p>

              {sub.cancel_at_period_end && (
                <div className="mt-4 p-3 rounded-xl bg-red-50 border border-red-200 text-left">
                  <p className="text-xs font-bold text-red-700">
                    ⚠ Abbonamento in cancellazione
                  </p>
                  <p className="text-[11px] text-red-600 mt-0.5">
                    Resterà attivo fino al {formatDate(sub.current_period_end)}.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* CTA: gestione avanzata */}
        <div className="mt-8 bg-white rounded-3xl p-6 border border-gray-100 shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase mb-1">Gestione avanzata</p>
            <h3 className="text-lg font-black text-gray-900">Vuoi cambiare piano, aggiungere pacchetti o vedere le fatture?</h3>
            <p className="text-sm text-gray-500 mt-1">
              Trovi tutto nella pagina completa dell'abbonamento.
            </p>
          </div>
          <button
            onClick={() => navigate("/abbonamento")}
            className="h-12 px-6 rounded-2xl text-sm font-bold text-white flex items-center gap-2 transition-all hover:scale-[1.02]"
            style={{
              background: "linear-gradient(135deg,#6C63FF,#8B85FF)",
              boxShadow: "0 4px 16px rgba(108,99,255,0.30)"
            }}
            data-testid="billing-manage-full-btn"
          >
            Gestisci abbonamento <ExternalLink className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default BillingOverview;
